import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Button } from 'common-src/components/base';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { Colors, ColorsNew } from 'common-src/styles';

import useAlert from 'src/hooks/useAlert';
import usePopup from 'src/hooks/usePopup';

import BasePopup from '../BasePopup';
import { EmailTemplateEnum, STEP_TITLES, STEPS, UserTypeEnum } from './constants';
import styles from './SetPasswordModal.module.scss';

const SetPasswordModal = ({ portalPatientId, onClose }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.CONFIRMATION);
  const [loading, setLoading] = useState(false);
  const { showAlert, AlertType } = useAlert();
  const { renderButtons } = usePopup();

  const handleConfirm = () => {
    setCurrentStep(STEPS.OPTIONS);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSendEmail = async () => {
    setLoading(true);
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/sendEmail',
        method: HttpMethods.Post,
        body: {
          user_id: portalPatientId,
          user_type: UserTypeEnum.PATIENT,
          template: EmailTemplateEnum.WELCOME,
        },
      });
      if (response.ok) {
        showAlert(AlertType.Success, '', 'Password reset email sent successfully.');
        onClose(true);
      } else {
        const error = await response.json();
        showAlert(AlertType.Error, '', error.message || 'Failed to send password reset email.');
      }
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Network error while sending email.');
    } finally {
      setLoading(false);
    }
  };

  const renderConfirmation = () => (
    <div className={styles.content}>
      <p className={styles.message}>
        Are you sure you want to set a new password for this patient?
      </p>
    </div>
  );

  const renderOptions = () => (
    <div className={styles.content}>
      <p className={styles.description}>How would you like to set the patient&apos;s password?</p>
    </div>
  );

  const getTitle = () => STEP_TITLES[currentStep] || STEP_TITLES[STEPS.CONFIRMATION];

  const renderContent = () => {
    switch (currentStep) {
      case STEPS.CONFIRMATION:
        return renderConfirmation();
      case STEPS.OPTIONS:
        return renderOptions();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === STEPS.CONFIRMATION) {
      return renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose: handleCancel,
        onSubmit: handleConfirm,
        isSubmitEnabled: !loading,
        submitButtonText: 'Continue',
        isLoading: loading,
      });
    }

    if (currentStep === STEPS.OPTIONS) {
      return (
        <div className={styles.buttonsContainer}>
          <Button
            id="send-email-button"
            text="Send password reset email to patient"
            backgroundColor={ColorsNew.blue}
            textColor={ColorsNew.white}
            onClick={handleSendEmail}
            disabled={loading}
            isLoading={loading}
          />
          <Button
            id="cancel-button"
            text="Cancel"
            backgroundColor={Colors.white}
            textColor={ColorsNew.darkGreen}
            borderColor={ColorsNew.darkGreen}
            onClick={handleCancel}
            disabled={loading}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <BasePopup id="set-password-modal" onClose={onClose} open title={getTitle()}>
      {renderContent()}
      {renderFooter()}
    </BasePopup>
  );
};

SetPasswordModal.propTypes = {
  portalPatientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SetPasswordModal;
